import { SeatsioSeatingChart } from "@seatsio/seatsio-react";
import { useEventContext } from "../../../context-state/EventContext";
import { Box, Button, Card, Stack, Tooltip, Typography } from "@mui/material";
import { useLocales } from "../../../locales";
import { fCurrency } from "../../../utils/minimal/formatNumber";
import { useState } from "react";
import SeatsTicketPricing from "./SeatsTicketPricing";
import { CircularProgress } from "@mui/material";
import { useEffect } from "react";
import checkoutStore from "../../../stores/checkout.store";

export const EventSeatsChart = ({ setActiveStep, setChosenTickets }) => {

    const { translate } = useLocales()
    const [isSelectionValid, setIsSelectionValid] = useState(false)
    const { eventPageContext } = useEventContext()
    const { event, account, tickets } = eventPageContext
    const [chosenSeats, setChosenSeats] = useState({})
    useEffect(() => {
        checkoutStore.init(event, tickets)
    }, [event, tickets])

    const seatsTickets = tickets.filter(ticket => !!ticket.seatsCategoryKey)
    // unique price levels
    const priceLevels = seatsTickets.reduce((acc, ticket) => {
        if (acc.includes(ticket.priceLevel)) {
            return acc
        }
        return [...acc, ticket.priceLevel]
    }, [])
    const multiLevelPricing = priceLevels.length > 1
    const ticketsPricesToPresent = seatsTickets.map(ticket => ({ price: Number(ticket.price), category: ticket.seatsCategoryLabel, color: ticket.seatsCategoryColor, quantity: ticket.quantity }))
    /* 
    reduce the tickets to this format:
        { category: 1, ticketTypes: [
        { ticketType: 'adult', price: 30, label: 'Adults' },
        { ticketType: 'child', price: 20, label: 'Children' },
        { ticketType: 'senior', price: 25, label: 'Senior', description: '65+ – Requires ID' }
    ]},
    */
    const ticketsWithPriceLevelsToPresent = seatsTickets.reduce((acc, ticket) => {
        const ticketType = ticket.priceLevel
        const label = ticket.priceLevel
        const price = Number(ticket.price)
        const category = ticket.seatsCategoryLabel
        const color = ticket.seatsCategoryColor
        const { quantity } = ticket
        const ticketTypeObj = { ticketType, price, label }
        const categoryObj = acc.find(obj => obj.category === category)
        if (!categoryObj) {
            return [...acc, { category, ticketTypes: [ticketTypeObj], color, quantity }]
        }
        categoryObj.ticketTypes.push(ticketTypeObj)
        return acc
    }, [])

    const pricing = multiLevelPricing ? ticketsWithPriceLevelsToPresent : ticketsPricesToPresent
    const onSeatSelected = (seat) => {
        if (multiLevelPricing) {
            const nimiTicket = tickets.find(ticket => ticket.seatsCategoryKey == seat.category.key && ticket.priceLevel == seat.selectedTicketType)
            const { selectionPerTicketType } = seat

            setChosenSeats(prevState => {
                return {
                    ...prevState,
                    [seat.id]: { seat, nimiTicket, preKnownAmount: seat.numSelected, selectionPerTicketType }
                }
            })
        } else {
            const nimiTicket = tickets.find(ticket => ticket.seatsCategoryKey == seat.category.key)
            setChosenSeats(prevState => {
                return {
                    ...prevState,
                    [seat.id]: { seat, nimiTicket, preKnownAmount: seat.numSelected }
                }
            })
        }

    }

    const seatsWorkspaceKey = account.seatsWorkspaceKey ? account.seatsWorkspaceKey : account.parent?.seatsWorkspaceKey

    const onSeatDeselected = (seat) => {
        if (!seat.numSelected) {
            setChosenSeats(prevState => {
                const newState = { ...prevState }
                delete newState[seat.id]
                return newState
            })
            return
        }
        // If we have 'seat.numSelected' meaning it's a seat which the user can choose multi from it, when they deselect we want to increase the amount and to to delete the whole seats
        const nimiTicket = tickets.find(ticket => ticket.seatsCategoryKey == seat.category.key)
        const { selectionPerTicketType } = seat
        setChosenSeats(prevState => {
            return {
                ...prevState,
                [seat.id]: { seat, nimiTicket, preKnownAmount: seat.numSelected, selectionPerTicketType, }
            }
        })
    }

    const extractParentType = (parent, parentLabel) => {

        if (parentLabel === 'בר') {
            return 'בר'
        }
        if (!parent?.type) {
            return ''
        }

        if (parent.type.toLowerCase() === 'table') {
            return 'שולחן'
        }
        if (parent.type.toLowerCase() === 'row') {
            return 'שורה'
        }
        return ''
    }

    const extractSeatIoData = (seatIoTicket) => {
        switch (seatIoTicket.objectType) {
            case 'GeneralAdmissionArea':
                return { labelOne: seatIoTicket.labels.own, labelTwo: '', amount: seatIoTicket.numSelected }
            case 'Seat':
                const parentNumber = seatIoTicket.labels?.parent?.match(/\d+/g)?.join('')
                const parentType = extractParentType(seatIoTicket.parent, seatIoTicket.labels?.parent)
                return { labelOne: `כיסא ${seatIoTicket.labels.own}`, labelTwo: `${parentType ? parentType : ""} ${parentNumber ? parentNumber : ''}` }
        }
        // TODO - add more types in the future
    }

    const onApproveSeats = () => {
        const keys = Object.keys(chosenSeats)
        const ticketsSeatsInfo = {}
        for (const key of keys) {
            const { seat, nimiTicket, preKnownAmount = 1, selectionPerTicketType } = chosenSeats[key]

            if (!ticketsSeatsInfo[nimiTicket._id]) {
                ticketsSeatsInfo[nimiTicket._id] = { amount: 0, info: { seats: [] } }
            }
            if (!selectionPerTicketType) {
                for (let i = 0; i < preKnownAmount; i++) {
                    ticketsSeatsInfo[nimiTicket._id].amount++
                    const { labelOne, labelTwo } = extractSeatIoData(seat)
                    ticketsSeatsInfo[nimiTicket._id].info.seats.push({
                        seatsIoTicket: seat,
                        id: seat.id,
                        label: seat.label,
                        seatLabel: labelOne,
                        rowLabel: labelTwo,
                        categoryLabel: seat.category?.label,
                        row: seat.labels?.parent,
                        actualSeat: seat.labels?.own
                    })
                }
            } else {
                // in this case we are handeling seats.io multi pricing in the same category for general admission areas that are not seats with specific labels
                for (const ticketType of Object.keys(selectionPerTicketType)) {
                    const amount = selectionPerTicketType[ticketType]
                    for (let i = 0; i < amount; i++) {
                        const ticketId = tickets.find(ticket => ticket.priceLevel == ticketType && ticket.seatsCategoryKey == seat.category.key)._id
                        ticketsSeatsInfo[ticketId] = ticketsSeatsInfo[ticketId] || { amount: 0, info: { seats: [] } }
                        ticketsSeatsInfo[ticketId].amount++
                        const { labelOne, labelTwo } = extractSeatIoData(seat)
                        ticketsSeatsInfo[ticketId].info.seats.push({
                            seatsIoTicket: seat,
                            id: seat.id,
                            label: seat.label,
                            seatLabel: labelOne,
                            rowLabel: labelTwo,
                            categoryLabel: seat.category?.label,
                            row: seat.labels?.parent,
                            actualSeat: seat.labels?.own,
                            ticketType
                        })
                    }
                }
            }

        }

        setChosenTickets(ticketsSeatsInfo)
        setActiveStep(1)
    }

    if (!tickets.length) {
        return (
            <Stack sx={{ my: 2, textAlign: 'center' }}>- אין כרטיסים למכירה -</Stack>
        )
    }
    const selectionValidators = []
    if (event.seatsIoOrphanSeats) {
        selectionValidators.push({ type: 'noOrphanSeats', mode: 'lenient' })
    }

    if (!checkoutStore.initialized) return <CircularProgress />
    return (
        <Card>
            <Stack sx={{ p: 2, gap: 2 }}>
                <Typography sx={{ color: 'text.secondary' }} variant="h4">מחירי כרטיסים</Typography>
                {/* {
                    priceLevels.map(priceLevel => {
                        return <div key={priceLevel}>
                            {multiLevelPricing && <span>{priceLevel}</span>}
                            <Stack direction={"row"}>
                                {seatsTickets.map(ticket => {
                                    if (ticket.priceLevel !== priceLevel) {
                                        return null
                                    }
                                    return (
                                        <Stack key={ticket._id} sx={{ width: "fit-content", display: "flex", flexDirection: "row", justifyContent: "space-between", p: 1, alignItems: "center" }}>
                                            <Box sx={{ width: "16px", height: "16px", backgroundColor: ticket.seatsCategoryColor, borderRadius: "16px" }} />
                                            <Stack direction="row">
                                                <Typography variant="body2" sx={{ ml: "15px", mr: "10px" }}>
                                                    {ticket.seatsCategoryLabel}
                                                </Typography>
                                                <Typography variant="body2">
                                                    {fCurrency(ticket.price)}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    )
                                })}
                            </Stack>
                        </div>
                    })
                } */}
                <SeatsTicketPricing pricing={pricing} />
                {event.isAllowCoupon && <Typography variant="caption" gutterBottom sx={{ display: 'block' }}>
                    *ניתן להכניס קוד קופון לאחר בחירת המקומות
                </Typography>}
                
                <Box sx={{ height: 500, overflow: 'auto' }}>
                    <SeatsioSeatingChart
                        showFullScreenButton={false}
                        showSectionPricingOverlay
                        workspaceKey={seatsWorkspaceKey}
                        event={event.seatsEventKey}
                        session="continue"
                        selectionValidators={selectionValidators}
                        language="he"
                        onSelectionInvalid={() => setIsSelectionValid(false)}
                        onSelectionValid={() => setIsSelectionValid(true)}
                        onRenderStarted={createdChart => {
                            // setChart(createdChart)

                        }}
                        onObjectDeselected={onSeatDeselected}
                        onObjectSelected={onSeatSelected}
                        region="eu"
                        pricing={pricing}
                        priceFormatter={fCurrency}
                        objectTooltip={{
                            confirmSelectionOnMobile: event.seatsIoConfirmSelectionOnMobile,
                        }}
                    />
                </Box>

                <Stack>
                    {
                        isSelectionValid && Object.keys(chosenSeats).length
                            ?
                            <Button size="large" variant="contained" onClick={onApproveSeats}>
                                {translate('event_page.to_order')}
                            </Button>
                            :
                            <Tooltip title={translate('event_page.please_choose_tickets')} placement="top">
                                <span>
                                    <Button size="large" sx={{ width: "100%" }} disabled variant="contained">
                                        {translate('event_page.to_order')}
                                    </Button>
                                </span>
                            </Tooltip>
                    }
                </Stack>
            </Stack>
        </Card>
    )
}
